/**
 * Oath Power Inquiry Service
 * API calls for power inquiry/search operations using the new Oath authentication
 * Routes to /oath/powerinquiry/* endpoints
 */

import { OathStorageKeys } from '../models/userModels';
import { oathRefreshToken } from './oathAuthService';

const BASE_URL = process.env.REACT_APP_BASE_URL;

/**
 * Get auth headers with JWT token
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem(OathStorageKeys.JWT_TOKEN);
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

/**
 * Authenticated fetch wrapper with auto-refresh on 401
 */
const authFetch = async (url, options = {}) => {
  const response = await fetch(url, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers,
    },
  });

  // If unauthorized, try to refresh token and retry
  if (response.status === 401) {
    const refreshResult = await oathRefreshToken();
    if (refreshResult.success) {
      // Retry with new token
      return fetch(url, {
        ...options,
        headers: {
          ...getAuthHeaders(),
          ...options.headers,
        },
      });
    }
  }

  return response;
};

/**
 * Parse API response - handles BaseResponse wrapper
 */
const parseResponse = async (response) => {
  const data = await response.json().catch(() => ({}));
  return data.result !== undefined ? data.result : data;
};

// ============================================
// SEARCH OPERATIONS
// ============================================

/**
 * Search individual powers with full filter support
 * Uses the new /powers endpoint that returns individual power records
 * @param {PowerInquirySearchRequest} request - Search request parameters
 * @returns {Promise<{success: boolean, data?: PowerSearchResponse, error?: string}>}
 */
export const searchPowers = async (request) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/powerinquiry/powers`, {
      method: 'POST',
      body: JSON.stringify(request),
    });

    const result = await parseResponse(response);

    if (!response.ok) {
      return {
        success: false,
        error: result.message || 'Failed to search powers',
        data: null
      };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Search powers error:', error);
    return { success: false, error: 'Network error', data: null };
  }
};

/**
 * Get aggregates (totalCount, totalBondAmount) for a power search
 * Used for parallel loading - call this alongside searchPowers with skipTotalCount/skipTotalAmount
 * @param {PowerInquirySearchRequest} request - Search request parameters (same as searchPowers)
 * @returns {Promise<{success: boolean, data?: {totalCount: number, totalBondAmount: number}, error?: string}>}
 */
export const getAggregates = async (request) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/powerinquiry/powers/aggregates`, {
      method: 'POST',
      body: JSON.stringify(request),
    });

    const result = await parseResponse(response);

    if (!response.ok) {
      return {
        success: false,
        error: result.message || 'Failed to get aggregates',
        data: null
      };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Get aggregates error:', error);
    return { success: false, error: 'Network error', data: null };
  }
};

/**
 * Get details for a specific power
 * @param {string} powerNo - Power number
 * @returns {Promise<{success: boolean, data?: PowerDetailDto, error?: string}>}
 */
export const getPowerDetails = async (powerNo) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/powerinquiry/powers/${encodeURIComponent(powerNo)}`);
    const result = await parseResponse(response);

    if (!response.ok) {
      return {
        success: false,
        error: result.message || 'Failed to get power details',
        data: null
      };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Get power details error:', error);
    return { success: false, error: 'Network error', data: null };
  }
};

/**
 * @deprecated Use searchPowers instead - this returns grouped summaries
 * Grouped power inquiry search (legacy endpoint)
 */
export const searchPowersGrouped = async (request) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/powerinquiry/search`, {
      method: 'POST',
      body: JSON.stringify(request),
    });

    const result = await parseResponse(response);

    if (!response.ok) {
      return {
        success: false,
        error: result.message || 'Failed to search powers',
        data: null
      };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Search powers grouped error:', error);
    return { success: false, error: 'Network error', data: null };
  }
};

/**
 * Search powers grouped by Report
 * Returns paginated report groups with aggregated power counts and totals
 * @param {PowerInquirySearchRequest} request - Search request parameters
 * @returns {Promise<{success: boolean, data?: ReportGroupedResponse, error?: string}>}
 */
export const searchPowersByReport = async (request) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/powerinquiry/powers/grouped`, {
      method: 'POST',
      body: JSON.stringify(request),
    });

    const result = await parseResponse(response);

    if (!response.ok) {
      return {
        success: false,
        error: result.message || 'Failed to search powers by report',
        data: null
      };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Search powers by report error:', error);
    return { success: false, error: 'Network error', data: null };
  }
};

// ============================================
// FILTER OPERATIONS
// ============================================

/**
 * Get available filter definitions for power inquiry
 * @returns {Promise<{success: boolean, data?: PowerInquiryFilter[], error?: string}>}
 */
export const getFilters = async () => {
  try {
    const response = await authFetch(`${BASE_URL}oath/powerinquiry/filters`);
    const result = await parseResponse(response);

    if (!response.ok) {
      return {
        success: false,
        error: result.message || 'Failed to get filters',
        data: []
      };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Get filters error:', error);
    return { success: false, error: 'Network error', data: [] };
  }
};

// ============================================
// EXPORT OPERATIONS
// ============================================

/**
 * Export power inquiry results to CSV (advanced search)
 * @param {PowerInquirySearchRequest} request - Search request parameters
 * @returns {Promise<{success: boolean, blob?: Blob, error?: string}>}
 */
export const exportPowersCsv = async (request) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/powerinquiry/export`, {
      method: 'POST',
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const result = await response.json().catch(() => ({}));
      return {
        success: false,
        error: result.message || 'Failed to export powers'
      };
    }

    const blob = await response.blob();
    return { success: true, blob };
  } catch (error) {
    console.error('Export powers CSV error:', error);
    return { success: false, error: 'Network error' };
  }
};

/**
 * Export power inquiry results to CSV (simple search)
 * @param {Object} params - Export parameters
 * @param {number} params.agencyId - Agency ID (required)
 * @param {boolean} params.includeSubAgencies - Include sub-agencies
 * @param {string} params.filter - Single filter name
 * @returns {Promise<{success: boolean, blob?: Blob, error?: string}>}
 */
export const exportPowersCsvSimple = async (params) => {
  try {
    const queryParams = new URLSearchParams();
    queryParams.append('agencyId', params.agencyId.toString());

    if (params.includeSubAgencies !== undefined) {
      queryParams.append('includeSubAgencies', params.includeSubAgencies.toString());
    }
    if (params.filter) {
      queryParams.append('filter', params.filter);
    }

    const response = await authFetch(`${BASE_URL}oath/powerinquiry/export?${queryParams}`);

    if (!response.ok) {
      const result = await response.json().catch(() => ({}));
      return {
        success: false,
        error: result.message || 'Failed to export powers'
      };
    }

    const blob = await response.blob();
    return { success: true, blob };
  } catch (error) {
    console.error('Simple export powers CSV error:', error);
    return { success: false, error: 'Network error' };
  }
};

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Download a blob as a file
 * @param {Blob} blob - The blob to download
 * @param {string} filename - The filename to use
 */
export const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// ============================================
// CONSTANTS
// ============================================

export const DATE_TYPES = {
  NONE: 'none',
  EXECUTION: 'execution',
  RECORDED: 'recorded',
  REPORT: 'report',
  DUE_DATES: 'duedates',
};

/**
 * View modes for power inquiry results
 */
export const VIEW_MODES = {
  INDIVIDUAL: 'individual',
  BY_REPORT: 'byReport',
};

export const FILTER_TYPES = {
  GROUP: 1,         // Radio button - mutually exclusive
  SPECIFIC: 2,      // Checkbox - can combine multiple
  GROUP_SCOPED: 3,  // Scoped to a group filter (e.g., AOL bucket filters require OpenLiability)
};

export const DEFAULT_PAGE_SIZE = 25;

/**
 * Sort field options for power search
 */
export const SORT_FIELDS = {
  POWER_NO: 'powerno',
  EXECUTION_DATE: 'executiondate',
  DUE_DATES: 'duedates',
  DEFENDANT: 'defendant',
  BOND_AMOUNT: 'bondamount',
  STATUS: 'status',
  COURT_CITY: 'courtcity',
  COUNTY: 'county',
  CASE_NUMBER: 'casenumber',
  EXECUTING_AGENT: 'executingagent',
  AGENCY_ID: 'agentId',
  BATCH_ID: 'batchid',
  REPORT_ID: 'reportid',
  REPORT_NUMBER: 'reportnumber',
  REPORT_DATE: 'reportdate',
  POWER_COUNT: 'powercount',
};

// ============================================
// TYPE DEFINITIONS (JSDoc)
// ============================================

/**
 * @typedef {Object} PowerInquirySearchRequest
 * @property {number[]} agencyIds - Array of agency IDs to search
 * @property {number[]} [executingAgentIds] - Optional executing agent IDs
 * @property {string} [defendantLastName] - Defendant last name (partial match)
 * @property {string} [defendantFirstName] - Defendant first name (partial match)
 * @property {string} [powerNumber] - Power number (exact match)
 * @property {number} [batchId] - Batch ID to filter by
 * @property {string} [reportNo] - Report number to filter by
 * @property {string} [startDate] - Start date (ISO 8601)
 * @property {string} [endDate] - End date (ISO 8601)
 * @property {string} dateType - Date type: 'none', 'execution', 'recorded', 'report', 'duedates'
 * @property {string[]} filters - Array of filter names to apply
 * @property {number} pageNumber - Page number (1-based)
 * @property {number} pageSize - Page size
 * @property {string} [sortBy] - Sort field: PowerNo, ExecutionDate, DueDates, Defendant, BondAmount, Status, BatchId, ReportId, ReportDate
 * @property {boolean} sortDescending - Sort direction
 */

/**
 * @typedef {Object} PowerSearchResponse
 * @property {PowerRecord[]} items - Individual power records
 * @property {number} totalCount - Total matching records
 * @property {number} pageNumber - Current page number
 * @property {number} pageSize - Page size
 * @property {number} totalPages - Total number of pages
 * @property {boolean} hasPreviousPage - Has previous page
 * @property {boolean} hasNextPage - Has next page
 */

/**
 * @typedef {Object} PowerRecord
 * @property {string} powerNo - Power number
 * @property {string} powerCheckDigit - Check digit
 * @property {number} batchId - Batch ID
 * @property {number} reportId - Report ID
 * @property {string} reportNo - Report number
 * @property {string} powerStatus - Power status code
 * @property {string} powerStatusDescription - Power status description
 * @property {string} transactionStatus - Transaction status code
 * @property {string} transactionStatusDescription - Transaction status description
 * @property {string} powerType - Power type code
 * @property {string} powerTypeDescription - Power type description
 * @property {number} bondAmount - Bond amount
 * @property {number} powerAmount - Power amount
 * @property {number} premiumAmount - Premium amount
 * @property {number} netDueAmount - Net due amount
 * @property {number} bufAmount - BUF amount
 * @property {string} executionDate - Execution date
 * @property {string} expirationDate - Expiration date
 * @property {string} dateRecorded - Date recorded
 * @property {string} transactionDate - Transaction date
 * @property {string} reportDate - Report date
 * @property {string} reportingDate - Reporting date
 * @property {string} defendantFirstName - Defendant first name
 * @property {string} defendantLastName - Defendant last name
 * @property {string} defendantFullName - Defendant full name
 * @property {string} city - City
 * @property {string} courtCity - Court city (CourtInfo.City), returned on search list rows
 * @property {number} countyId - County ID
 * @property {string} countyName - County name
 * @property {string} state - State
 * @property {string} caseNumber - Case number
 * @property {number} agencyId - Agency ID
 * @property {string} agencyName - Agency name
 * @property {number} executingSubAgentId - Executing sub-agent ID
 * @property {string} executingInitials - Executing agent initials
 * @property {string} executingAgent - Executing agent display name
 * @property {boolean} missing - Is missing flag
 * @property {boolean} expired - Is expired flag
 * @property {string} rewriteId - Rewrite ID (if rewritten to)
 * @property {string} rewriteFromId - Rewrite from ID (if rewritten from)
 * @property {boolean} wasEverForfeiture - Was ever in forfeiture
 * @property {boolean} wasEverJudgment - Was ever in judgment
 */

/**
 * @typedef {Object} PowerInquiryFilter
 * @property {string} filterName - Filter identifier (use in search requests)
 * @property {string} label - Short display label for UI (e.g., "All Powers")
 * @property {string} [description] - Longer description explaining what this filter does
 * @property {string} [category] - Category grouping for UI (e.g., "Status", "Transfer", "Conditions")
 * @property {number} filterType - 1 = Radio (group), 2 = Checkbox (specific)
 * @property {number} displayOrder - Sort order for display
 * @property {string[]} [statusCodes] - Status codes matched by this filter
 * @property {string} [filterDescription] - DEPRECATED: Use label instead
 */
